import { TEST_CDK_URL } from '@/constants/constants';
import { apiFetch } from '@final-commerce/jwt-storage-lib';
import { User } from '@/types';

const API_KEY_URL = `${TEST_CDK_URL}organization-api-key/`;

export enum Permissions {
  CREATE = 'create',
  READ = 'read',
  UPDATE = 'update',
  DELETE = 'delete',
}
export type ApiKey = {
  _id: string;
  companyId: string;
  publicApiKey: string;
  index: number;
  name?: string;
  createdBy?: string;
  permissions: {
    create?: boolean;
    read?: boolean;
    update?: boolean;
    delete?: boolean;
  };
  createdAt?: string;
  updatedAt?: string;
  users?: User[];
};

export type CreateOrgApiKeyRequest = {
  organizationId: string;
  name: string;
  permissions: string[];
};

export async function getApiKeys(organizationId: string, search?: string): Promise<ApiKey[]> {
  try {
    const response = await apiFetch(
      `${API_KEY_URL}${organizationId}${search ? `?search=${encodeURIComponent(search)}` : ''}`,
      {
        method: 'GET',
      },
    );
    if (!response?.ok) {
      throw new Error('Error fetching API keys');
    }
    const data = await response.json();
    return data;
  } catch (err) {
    throw new Error('Error fetching API keys');
  }
}

export async function createApiKey({
  organizationId,
  name,
  permissions,
}: CreateOrgApiKeyRequest): Promise<string> {
  try {
    const response = await apiFetch(`${API_KEY_URL}${organizationId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, permissions }),
    });
    if (!response?.ok) {
      throw new Error('Error creating API key');
    }

    // Read response as text first (can only read body once)
    const text = await response.text();

    // Try to parse as JSON (NestJS may return string as JSON with quotes)
    try {
      const data = JSON.parse(text);
      // If parsed successfully and it's a string, return it
      // Otherwise, try to extract from object
      return typeof data === 'string' ? data : data?.key || data?.apiKey || String(data);
    } catch {
      // If JSON parsing fails, return the text as-is (plain text response)
      return text;
    }
  } catch (err) {
    throw new Error('Error creating API key');
  }
}

export type DeleteApiKeyRequest = {
  organizationId: string;
  apiKeyId: string;
};

export async function deleteApiKey({
  organizationId,
  apiKeyId,
}: DeleteApiKeyRequest): Promise<{ success: boolean }> {
  try {
    const response = await apiFetch(`${API_KEY_URL}${organizationId}/${apiKeyId}`, {
      method: 'DELETE',
    });
    if (!response?.ok) {
      throw new Error('Error deleting API key');
    }
    const data = await response.json();
    return data;
  } catch (err) {
    throw new Error('Error deleting API key');
  }
}
